import { useEffect } from 'react';

const CABLE_URL = import.meta.env.VITE_CABLE_URL ?? '/cable';
const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 30_000;

type CableMessage = { type: string; [key: string]: unknown };

let socket: WebSocket | null = null;
let reconnectAttempts = 0;
let reconnectTimer: number | null = null;
const subscriptions = new Set<string>();
const listeners = new Map<string, Set<(m: CableMessage) => void>>();

const flushSubscriptions = (ws: WebSocket) => {
  subscriptions.forEach((identifier) => {
    try {
      ws.send(JSON.stringify({ command: 'subscribe', identifier }));
    } catch (_error) {
      // A closed socket is retried through the normal reconnect path.
    }
  });
};

const scheduleReconnect = () => {
  if (reconnectTimer != null) return;
  const delay = Math.min(RECONNECT_BASE * 2 ** reconnectAttempts, RECONNECT_MAX);
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    reconnectAttempts += 1;
    socket = null;
    ensureSocket(localStorage.getItem('canapyra-jwt'));
  }, delay);
};

const ensureSocket = (token: string | null): WebSocket => {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return socket;
  }
  const url = token ? `${CABLE_URL}?token=${encodeURIComponent(token)}` : CABLE_URL;
  socket = new WebSocket(url);

  socket.addEventListener('open', () => {
    reconnectAttempts = 0;
    if (socket) flushSubscriptions(socket);
  });

  socket.addEventListener('message', (e) => {
    try {
      const payload = JSON.parse(e.data) as { identifier?: string; message?: CableMessage; type?: string };
      if (payload.type === 'ping' || payload.type === 'welcome' || payload.type === 'confirm_subscription') return;
      if (!payload.identifier || !payload.message) return;
      listeners.get(payload.identifier)?.forEach((fn) => fn(payload.message!));
    } catch (_error) {
      // Invalid messages are ignored so one bad frame does not break the channel.
    }
  });

  socket.addEventListener('close', scheduleReconnect);
  socket.addEventListener('error', () => socket?.close());

  return socket;
};

export const subscribeChannel = (
  channel: string,
  params: Record<string, unknown>,
  onMessage: (m: CableMessage) => void,
): (() => void) => {
  const identifier = JSON.stringify({ channel, ...params });
  const listenerSet = listeners.get(identifier) ?? new Set();
  listenerSet.add(onMessage);
  listeners.set(identifier, listenerSet);
  subscriptions.add(identifier);

  const ws = ensureSocket(localStorage.getItem('canapyra-jwt'));
  if (ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify({ command: 'subscribe', identifier }));
    } catch (_error) {
      // The socket can close between the state check and the send.
    }
  }

  return () => {
    listenerSet.delete(onMessage);
    if (listenerSet.size === 0) {
      listeners.delete(identifier);
      subscriptions.delete(identifier);
      try {
        ws.send(JSON.stringify({ command: 'unsubscribe', identifier }));
      } catch (_error) {
        // The socket may already be closed during component cleanup.
      }
    }
  };
};

export const closeCable = (): void => {
  reconnectAttempts = 0;
  if (reconnectTimer != null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  subscriptions.clear();
  listeners.clear();
  socket?.close();
  socket = null;
};

export const useChannel = (
  channel: string,
  params: Record<string, unknown>,
  onMessage: (m: CableMessage) => void,
): void => {
  useEffect(() => {
    if (!channel) return;
    return subscribeChannel(channel, params, onMessage);
    // The serialized value gives stable subscriptions for semantically equal params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, JSON.stringify(params), onMessage]);
};
